import { Alert, AlertEvent, AlertEventKind, Alerts as AlertsApi, AlertStatus, AlertType, Device, enumKeys, getAlertTypeDisplayName, ResolveBody } from "@app/shared";
import { FC, useCallback, useState } from "react";
import { toastError, toastSuccess } from "../../../helpers";
import { useConfirmDialog, useEditModelDialog, useFacilityTZ, useFetch } from "../../../hooks";
import { IconLink, Icons, PageContainer, SectionIntro, SortableTable, StatusDot } from "../../shared";
import { DropDownMenu } from "../../shared/DropDownMenu";
import { TextArea } from "../../shared/inputs";
import { alertTypeLabel, enumLabel, severityDotStatus, severityLabel, zonedTime } from "../notifications/alertDisplay";

const alertTypeFilterOptions = enumKeys(AlertType).map((key) => ({ value: (AlertType[key] as number).toString(), label: getAlertTypeDisplayName(key) ?? key }));

const statusFilterOptions = [
    { value: AlertStatus.Active.toString(), label: "Active" },
    { value: AlertStatus.Acknowledged.toString(), label: "Acknowledged" },
    { value: AlertStatus.Resolved.toString(), label: "Resolved" },
];

interface Props {
    device: Device;
}

/**
 * Per-device Health tab: every alert this device has raised, active and recent, with the event
 * timeline for each one.
 *
 * Deliberately the same components, wording and controls as the fleet-wide Alerts page — PageContainer,
 * SectionIntro, SortableTable, StatusDot, the resolve-note dialog — because it is the same data seen
 * through a narrower window. A hand-rolled list here meant a severity read as a coloured chip in one
 * place and a status dot in the other, timestamps with no timezone, resolve notes collected by
 * window.prompt, and no way to sort or filter what a busy device had reported.
 */
export const DeviceHealthTab: FC<Props> = ({ device }) => {
    const [expandedAlertId, setExpandedAlertId] = useState<string | undefined>();
    const [events, setEvents] = useState<Record<string, AlertEvent[]>>({});

    // The device's own location's zone, not the viewer's: this tab is about one physical unit, and
    // "was it offline overnight" is a question about the time where it's installed. (The fleet-wide
    // Alerts page spans locations, so it uses the viewer's zone instead.)
    const [facilityTZ] = useFacilityTZ(device.facilityId!);

    // Two calls because the list endpoint returns only non-resolved alerts unless a status is named,
    // and the tab doubles as recent history — an on-call op needs to see what has been broken lately,
    // not just what is broken right now. The two sets are disjoint by definition, so no dedupe.
    const [alerts, , alertHelpers] = useFetch(
        async () => {
            if (!device.id) return [];
            const [open, resolved] = await Promise.all([
                AlertsApi.list(undefined, undefined, device.id, undefined, undefined, 100),
                AlertsApi.list(undefined, undefined, device.id, AlertStatus.Resolved, undefined, 50),
            ]);
            return [...(open ?? []), ...(resolved ?? [])];
        },
        [device.id]
    );

    const resolveDialog = useEditModelDialog(ResolveBody);
    const confirmDialog = useConfirmDialog();

    const toggleTimeline = useCallback(
        async (alertId?: string) => {
            if (!alertId) return;
            if (expandedAlertId === alertId) {
                setExpandedAlertId(undefined);
                return;
            }
            setExpandedAlertId(alertId);
            // Fetched once per alert and kept: the timeline of a past alert can't change, and the only
            // one that can is refetched with the table.
            if (!events[alertId]) {
                const result = await AlertsApi.getEvents(alertId);
                setEvents((curr) => ({ ...curr, [alertId]: result ?? [] }));
            }
        },
        [expandedAlertId, events]
    );

    const reload = useCallback(async () => {
        // Drop the cached timelines along with the rows: an acknowledge or a resolve adds an event to
        // the very alert whose timeline is most likely on screen.
        setEvents({});
        alertHelpers.refreshData();
    }, [alertHelpers]);

    const handleAcknowledgeOnClick = (alert?: Alert) => {
        if (!alert) return;
        confirmDialog.show("Acknowledge Alert?", `${alert.message ?? alertTypeLabel(alert.alertType)} will be marked as acknowledged.`, "Acknowledge", async () => {
            try {
                await AlertsApi.acknowledge(alert.id);
                await reload();
                toastSuccess("Alert Acknowledged");
            } catch (e) {
                toastError("Error Acknowledging Alert", e + "");
            }
        });
    };

    const handleResolveOnClick = (alert?: Alert) => {
        if (!alert) return;
        resolveDialog.show(
            async (body: Partial<ResolveBody>) => {
                await AlertsApi.resolve(alert.id, body as ResolveBody);
                await reload();
                toastSuccess("Alert Resolved");
            },
            "Resolve Alert",
            new ResolveBody()
        );
    };

    if (!device.id) return null;

    return (
        <>
            <PageContainer
                Icon={Icons.AlertTriangle}
                title="Device Health"
                titleRight={
                    <IconLink Icon={Icons.Refresh} onClick={reload}>
                        Refresh
                    </IconLink>
                }
            >
                <SectionIntro title="What this device has reported, and what's been done about it.">
                    <p>
                        An alert opens on its own when this device reports a fault or stops checking in, and it closes on its
                        own once the problem stops — shown as resolved with <strong>(auto)</strong>. Resolved alerts stay
                        listed here so you can see what has been wrong recently, not only what is wrong now.
                    </p>
                    <p>
                        <strong>Acknowledge</strong> records that someone is looking at it. <strong>Resolve</strong> says it's
                        dealt with and lets you leave a note about what was done. <strong>Show Timeline</strong> lists every
                        transition the alert went through, in this location's timezone.
                    </p>
                </SectionIntro>
                <SortableTable
                    initialSortKey="openedAtUtc"
                    initialSortDir="desc"
                    isLoading={alertHelpers.isLoading}
                    emptyValue={<p className="m-5">No Alert Activity For This Device</p>}
                    expandedRowId={expandedAlertId}
                    renderExpandedContent={(alert) => (
                        <>
                            {/* Re-observation is a count on the alert, not a row per evaluation — so it
                                is stated once here instead of filling the table below. LastEvaluatedAtUtc
                                is when the most recent one was; on a resolved row still being seen, that
                                is also what flap suppression looks like from the outside. */}
                            {!!alert.observationCount && (
                                <p className="mx-3 mt-3 text-sm text-muted-body-text">
                                    Condition re-observed {alert.observationCount.toLocaleString()}{" "}
                                    {alert.observationCount === 1 ? "time" : "times"} since this alert opened, most recently{" "}
                                    {zonedTime(alert.lastEvaluatedAtUtc, facilityTZ)}.
                                </p>
                            )}
                            <SortableTable<AlertEvent>
                                columns={[
                                    {
                                        header: "Time",
                                        width: 200,
                                        dataKey: "occurredAtUtc",
                                        renderFunc: (e) => zonedTime(e?.occurredAtUtc, facilityTZ),
                                    },
                                    {
                                        header: "Event",
                                        width: 200,
                                        dataKey: "kind",
                                        renderFunc: (e) => (e ? enumLabel(AlertEventKind[e.kind] ?? "") : ""),
                                    },
                                ]}
                                // Alerts opened before the counter landed carry one ConditionStillPresent
                                // or FlapSuppressed row per evaluation — hundreds on a multi-day alert,
                                // which is what made this timeline unreadable. Neither kind is written any
                                // more; the historical rows are hidden rather than deleted so the
                                // transitions around them stay legible.
                                data={((alert.id ? events[alert.id] : undefined) ?? []).filter(
                                    (e) =>
                                        e.kind !== AlertEventKind.ConditionStillPresent &&
                                        e.kind !== AlertEventKind.FlapSuppressed
                                )}
                                initialSortKey="occurredAtUtc"
                                initialSortDir="asc"
                                emptyValue={<p className="m-3 text-muted-body-text">No Events Recorded</p>}
                            />
                        </>
                    )}
                    columns={[
                        {
                            header: "Severity",
                            width: 110,
                            dataKey: "severity",
                            renderFunc: (a) => (
                                <div className="flex items-center gap-2">
                                    <StatusDot status={severityDotStatus(a?.severity)} sizeClasses="w-3 h-3" />
                                    <span>{severityLabel(a?.severity)}</span>
                                </div>
                            ),
                        },
                        {
                            header: "Opened",
                            width: 180,
                            dataKey: "openedAtUtc",
                            renderFunc: (a) => zonedTime(a?.openedAtUtc, facilityTZ),
                        },
                        {
                            header: "Type",
                            width: 180,
                            dataKey: "alertType",
                            filterable: true,
                            // Select, not text: the cell's underlying value is the numeric enum, so a
                            // text filter would only match what the user can't see.
                            filterType: "select",
                            filterOptions: alertTypeFilterOptions,
                            sortFunc: (a, b) => alertTypeLabel(a?.alertType).localeCompare(alertTypeLabel(b?.alertType)),
                            renderFunc: (a) => (a ? alertTypeLabel(a.alertType) : ""),
                        },
                        {
                            header: "Status",
                            width: 130,
                            dataKey: "status",
                            filterable: true,
                            filterType: "select",
                            filterOptions: statusFilterOptions,
                            renderFunc: (a) => (a ? enumLabel(AlertStatus[a.status] ?? "") : ""),
                        },
                        {
                            header: "Message",
                            width: 400,
                            dataKey: "message",
                            filterable: true,
                            filterType: "text",
                            renderFunc: (a) => (
                                <>
                                    <div>{a?.message ?? "—"}</div>
                                    {a?.status === AlertStatus.Resolved && (
                                        <div className="text-xs text-muted-body-text mt-1">
                                            Resolved {zonedTime(a.resolvedAtUtc, facilityTZ)}
                                            {/* Who did it, matching what the notification emails say. autoResolved and a
                                                name are mutually exclusive — nobody resolves an alert that cleared itself. */}
                                            {a.autoResolved ? " (auto)" : a.resolvedByName ? ` by ${a.resolvedByName}` : ""}
                                            {a.resolveNote ? ` — ${a.resolveNote}` : ""}
                                        </div>
                                    )}
                                    {a?.status === AlertStatus.Acknowledged && (
                                        <div className="text-xs text-muted-body-text mt-1">
                                            Acknowledged {zonedTime(a.acknowledgedAtUtc, facilityTZ)}
                                            {a.acknowledgedByName ? ` by ${a.acknowledgedByName}` : ""}
                                        </div>
                                    )}
                                </>
                            ),
                        },
                        {
                            header: "",
                            width: 25,
                            renderFunc: (a) => (
                                <DropDownMenu>
                                    {a?.status === AlertStatus.Active && (
                                        <IconLink Icon={Icons.CircleSuccess} onClick={() => handleAcknowledgeOnClick(a)}>
                                            Acknowledge
                                        </IconLink>
                                    )}
                                    {a?.status !== AlertStatus.Resolved && (
                                        <IconLink Icon={Icons.Success} onClick={() => handleResolveOnClick(a)}>
                                            Resolve
                                        </IconLink>
                                    )}
                                    <IconLink Icon={expandedAlertId === a?.id ? Icons.CaretUp : Icons.CaretDown} onClick={() => toggleTimeline(a?.id)}>
                                        {expandedAlertId === a?.id ? "Hide Timeline" : "Show Timeline"}
                                    </IconLink>
                                </DropDownMenu>
                            ),
                        },
                    ]}
                    data={alerts}
                    pagination
                    defaultPageSize={25}
                    pageSizeOptions={[10, 25, 50, 100]}
                    filtering
                />
            </PageContainer>
            {confirmDialog.renderDialog()}
            {resolveDialog.renderDialog((_model, helpers) => (
                <div className="p-3">
                    <TextArea label="Resolve Note (optional)" rows={4} placeholder="What was done to fix this?" {...helpers.bindingsFor("note")} />
                </div>
            ))}
        </>
    );
};
